import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Handshake, TrendingUp, Users, Settings, Target, UserPlus, ArrowUpRight, BarChart3, Activity, PieChart } from "lucide-react";
import { Button, Reveal, FadeUp, SectionHeader, Marquee } from "../components/primitives";
import { EcosystemDiagram } from "../components/EcosystemDiagram";
import { useLang } from "../i18n";

const solIcons = [Handshake, TrendingUp, Users, Settings, Target, UserPlus];

const Home = () => {
  const { t } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const blobY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const blobScale = useTransform(scrollYProgress, [0, 1], [1, 1.25]);

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-28 pb-16 bg-wanm-offwhite grain">
        <motion.div style={{ y: blobY, scale: blobScale }}
          className="absolute -right-40 -top-20 w-[900px] h-[900px] rounded-full opacity-25 blur-[80px] pointer-events-none"
          aria-hidden>
          <div className="w-full h-full wanm-gradient-bg rounded-full" />
        </motion.div>
        <img src="/logos/isotype.png" alt="" aria-hidden
          className="absolute right-[-6%] bottom-[-8%] w-[45vw] max-w-[620px] opacity-[0.08] pointer-events-none select-none" />

        <div className="relative z-10 mx-auto max-w-[1500px] px-5 md:px-10 w-full">
          <FadeUp>
            <span className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-[0.25em] wanm-gradient-text">
              <span className="w-8 h-[2px] wanm-gradient-bg inline-block" />{t.tagline}
            </span>
          </FadeUp>
          <h1 className="mt-6 text-6xl sm:text-7xl lg:text-[8.5rem] font-black tracking-tighter leading-[0.86] text-wanm-dark">
            <Reveal>{t.home.heroLines[0]}</Reveal>
            <span className="block wanm-gradient-text"><Reveal delay={0.12}>{t.home.heroLines[1]}</Reveal></span>
          </h1>
          <FadeUp delay={0.25}>
            <p className="mt-8 max-w-2xl text-lg md:text-2xl text-wanm-dark/70 leading-relaxed">{t.home.heroSub}</p>
          </FadeUp>
          <FadeUp delay={0.35}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button variant="primary" to="/contact" testId="hero-cta-work">{t.cta.work}</Button>
              <Button variant="outline" to="/for-publishers" testId="hero-cta-publisher">{t.cta.publisher}</Button>
            </div>
          </FadeUp>
        </div>

        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-wanm-dark/40"
          animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
          <span className="text-xs font-bold uppercase tracking-widest">{t.home.scroll}</span>
          <div className="w-5 h-8 rounded-full border-2 border-wanm-dark/30 flex justify-center pt-1.5">
            <div className="w-1 h-2 rounded-full bg-wanm-dark/40" />
          </div>
        </motion.div>
      </section>

      {/* ECOSYSTEM */}
      <section className="py-24 md:py-36 bg-white">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <SectionHeader over={t.home.ecoTitle} title={t.home.trustTitle} sub={t.home.ecoSub} align="center" />
          <div className="mt-20">
            <EcosystemDiagram nodes={t.home.home?.nodes || t.home.nodes} />
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-24 md:py-32 bg-wanm-dark text-white relative overflow-hidden">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-sm font-black uppercase tracking-[0.25em] text-wanm-orange">{t.home.trustOver}</span>
            <h2 className="mt-5 text-4xl md:text-6xl font-black tracking-tight leading-[0.95]">
              <Reveal>{t.home.trustTitle}</Reveal>
            </h2>
          </div>
          <FadeUp delay={0.1}>
            <p className="text-xl md:text-2xl text-white/70 leading-relaxed">{t.home.trustBody}</p>
          </FadeUp>
        </div>
      </section>

      {/* TWO-SIDED */}
      <section className="py-24 md:py-36 bg-white">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <SectionHeader over={t.home.twoOver} title="Two audiences. One ecosystem." />
          <div className="mt-16 grid lg:grid-cols-2 gap-8">
            {[
              { title: t.home.brandCardTitle, body: t.home.brandCardBody, cta: t.cta.explore, to: "/for-brands", dark: true, img: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200" },
              { title: t.home.pubCardTitle, body: t.home.pubCardBody, cta: t.cta.join, to: "/for-publishers", dark: false, img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200" },
            ].map((c, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <Link to={c.to} data-testid={`audience-card-${i}`}
                  className={`group relative block rounded-3xl overflow-hidden p-10 md:p-14 min-h-[440px] flex flex-col justify-end transition-all duration-500 hover:scale-[1.01] ${c.dark ? "bg-wanm-dark text-white" : "wanm-gradient-bg text-white"}`}>
                  <img src={c.img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-30 group-hover:scale-105 transition-all duration-700" />
                  <div className="relative z-10">
                    <h3 className="text-4xl md:text-5xl font-black tracking-tight">{c.title}</h3>
                    <p className="mt-4 text-lg md:text-xl text-white/80 max-w-md">{c.body}</p>
                    <span className="mt-8 inline-flex items-center gap-2 font-bold text-lg group-hover:gap-3 transition-all">
                      {c.cta} <ArrowUpRight size={22} />
                    </span>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <Marquee words="PERFORMANCE MARKETING" />

      {/* SOLUTIONS */}
      <section className="py-24 md:py-36 bg-wanm-offwhite">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <SectionHeader over={t.home.solOver} title={t.home.solTitle} />
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.solutions.map((s, i) => {
              const Icon = solIcons[i];
              return (
                <FadeUp key={i} delay={(i % 3) * 0.08}>
                  <Link to="/solutions" data-testid={`solution-card-${i}`}
                    className="group relative block bg-white rounded-2xl p-8 h-full border border-white hover:shadow-[0_16px_48px_rgba(252,12,201,0.12)] transition-all duration-400 overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1 wanm-gradient-bg scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-400" />
                    <div className="w-14 h-14 rounded-xl bg-wanm-offwhite flex items-center justify-center mb-6 group-hover:wanm-gradient-bg transition-all duration-400">
                      <Icon className="text-wanm-red group-hover:text-white transition-colors" size={26} />
                    </div>
                    <h3 className="text-2xl font-black text-wanm-dark">{s.t}</h3>
                    <p className="mt-3 text-wanm-dark/60 leading-relaxed">{s.d}</p>
                  </Link>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 md:py-36 bg-white">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <SectionHeader over={t.home.howOver} title={t.home.howTitle} />
          <div className="mt-16 space-y-2">
            {t.steps.map((s, i) => (
              <FadeUp key={i} delay={i * 0.05}>
                <div className="group grid md:grid-cols-[120px_1fr_2fr] gap-4 md:gap-10 items-center py-8 border-t border-wanm-dark/10 hover:border-wanm-pink transition-colors">
                  <span className="text-5xl md:text-6xl font-black text-wanm-dark/15 group-hover:wanm-gradient-text transition-all">{s.n}</span>
                  <h3 className="text-2xl md:text-3xl font-black text-wanm-dark">{s.t}</h3>
                  <p className="text-lg text-wanm-dark/60">{s.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* PERFORMANCE */}
      <section className="py-24 md:py-36 bg-wanm-dark text-white relative overflow-hidden">
        <img src="/logos/isotype.png" alt="" aria-hidden className="absolute left-[-5%] top-[10%] w-[30vw] max-w-[420px] opacity-[0.06]" />
        <div className="mx-auto max-w-[1500px] px-5 md:px-10 relative z-10">
          <SectionHeader over={t.home.perfOver} title={t.home.perfTitle} sub={t.home.perfBody} light />
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.perfPoints.map((p, i) => {
              const Icon = [BarChart3, Target, Activity, Settings, TrendingUp, PieChart][i];
              return (
                <FadeUp key={i} delay={(i % 3) * 0.08}>
                  <div className="rounded-2xl border border-white/10 p-8 bg-white/[0.03] hover:bg-white/[0.06] transition-colors" data-testid={`perf-card-${i}`}>
                    <Icon className="text-wanm-orange mb-6" size={28} />
                    <h3 className="text-xl font-bold">{p}</h3>
                    <div className="mt-5 flex items-end gap-1.5 h-14">
                      {[40, 65, 50, 80, 60, 95].map((h, j) => (
                        <motion.div key={j} className="flex-1 rounded-t wanm-gradient-bg"
                          initial={{ height: 0 }} whileInView={{ height: `${h}%` }} viewport={{ once: true }}
                          transition={{ duration: 0.6, delay: j * 0.06 }} />
                      ))}
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-28 md:py-40 wanm-gradient-bg relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="relative z-10 mx-auto max-w-5xl px-5 md:px-10 text-center">
          <h2 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tighter text-white leading-[0.95]">
            <Reveal>{t.home.finalTitle}</Reveal>
          </h2>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="dark" to="/contact" testId="final-cta-talk">{t.cta.talk}</Button>
            <Button variant="outlineLight" to="/for-publishers" testId="final-cta-publisher">{t.cta.publisher}</Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
